import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
    DeepPartial,
    MoneyMapRuleWithAccountDetails,
} from '@fruitful/types';
import { Fragment } from 'react';

import { DropLine } from './DropLine';
import { GROUP_GOAL_ROW_MIN_HEIGHT } from './goalsWaterfallConstants';
import type { DropIndicator } from './goalsWaterfallTypes';
import { GroupGoalRow } from './GroupGoalRow';
import { PlaceholderSection } from './PlaceholderSection';

interface SortableGroupGoalProps {
    rule: DeepPartial<MoneyMapRuleWithAccountDetails>;
    groupId: string;
    dropIndicator: DropIndicator;
    isSelected?: boolean;
    onPress?: () => void;
    onPressPercentage?: () => void;
}

export const SortableGroupGoal = ({
    rule,
    groupId,
    dropIndicator,
    isSelected = false,
    onPress,
    onPressPercentage,
}: SortableGroupGoalProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        setActivatorNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: rule.id ?? '',
        data: { type: 'group-goal', groupId },
    });

    const showLineBefore =
        dropIndicator?.kind === 'line-before' &&
        dropIndicator.targetId === rule.id;
    const showLineAfter =
        dropIndicator?.kind === 'line-after' &&
        dropIndicator.targetId === rule.id;

    return (
        <Fragment>
            {showLineBefore && <DropLine />}
            {/* Native div: Tamagui YStack + transforms breaks @dnd-kit sortable layout on web. */}
            <div
                ref={setNodeRef}
                data-goals-waterfall-draggable-id={rule.id ?? ''}
                style={{
                    transform: CSS.Transform.toString(transform),
                    transition: transition ?? undefined,
                    marginBottom: 8,
                }}
            >
                {isDragging ? (
                    <PlaceholderSection
                        height={GROUP_GOAL_ROW_MIN_HEIGHT}
                        borderRadius={12}
                    />
                ) : (
                    <GroupGoalRow
                        rule={rule}
                        dragHandleProps={{
                            setActivatorNodeRef,
                            listeners,
                            attributes,
                        }}
                        isSelected={isSelected}
                        onPress={onPress}
                        onPressPercentage={onPressPercentage}
                    />
                )}
            </div>
            {showLineAfter && <DropLine />}
        </Fragment>
    );
};
